// ============================================================================
// TeamFormDialog — wizard creer / modifier une equipe (AD-B)
// ============================================================================
// Porte des PoC new-team-dialog.tsx + edit-team-dialog.tsx sur GsWizardDialog
// (AD-0, @gs/ui) : 2 etapes comme le PoC. Le selecteur de responsable = GsSelect
// (AD-0) et n'apparait qu'en EDITION (parite PoC : director NULL a la creation).
// STOP-8 : assigner un responsable est une affectation OPERATIONNELLE decouplee
// du role UCBR — aucun libelle ne promet un changement d'acces.
//
// Etat formulaire : corps interne monte uniquement si open — remontage = re-seed
// naturel depuis initial (edition A -> B sans fuite de valeurs).

import { useState } from 'react';
import { GsWizardDialog, GsSelect, GS_COMPONENTS, type GsWizardStep } from '@gs/ui';
import { useTranslation } from '../../i18n';
import type { AdminAssignablePerson, TeamUpdatePayload } from '../../hooks/useAdminTeams';

export interface TeamFormValues {
  name: string;
  department: string;
  headcount: string;
  directorEmail: string;
}

const EMPTY_VALUES: TeamFormValues = { name: '', department: '', headcount: '0', directorEmail: '' };

/** '12' -> 12 ; vide/invalide -> 0 (le serveur re-valide int >= 0). */
function parseHeadcount(raw: string): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function TeamFormDialog({
  open,
  mode,
  initial,
  assignable,
  isPending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  /** Valeurs de depart en edition — ignore en creation. */
  initial?: TeamFormValues;
  assignable: AdminAssignablePerson[];
  isPending: boolean;
  onClose: () => void;
  onSubmit: (payload: TeamUpdatePayload) => void;
}) {
  if (!open) return null;
  return (
    <TeamFormDialogBody
      open={open}
      mode={mode}
      initial={initial}
      assignable={assignable}
      isPending={isPending}
      onClose={onClose}
      onSubmit={onSubmit}
    />
  );
}

function TeamFormDialogBody({
  open,
  mode,
  initial,
  assignable,
  isPending,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: TeamFormValues;
  assignable: AdminAssignablePerson[];
  isPending: boolean;
  onClose: () => void;
  onSubmit: (payload: TeamUpdatePayload) => void;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<TeamFormValues>(() =>
    mode === 'edit' && initial ? initial : EMPTY_VALUES,
  );

  const set = (patch: Partial<TeamFormValues>) => setValues((v) => ({ ...v, ...patch }));

  // Responsable actuel absent du pool (fiche legacy) : on l'ajoute pour que la
  // valeur reste affichable — le serveur re-resout la denorm au write.
  const directorOptions = assignable.map((p) => ({ value: p.email, label: p.fullName ?? p.email }));
  if (values.directorEmail && !assignable.some((p) => p.email === values.directorEmail)) {
    directorOptions.push({ value: values.directorEmail, label: values.directorEmail });
  }

  const labelCls = 'text-meta font-medium text-slate-600 dark:text-slate-300';

  const identityStep: GsWizardStep = {
    id: 'identity',
    title: t('gest:admin.teams.form.stepIdentity'),
    canProceed: () => values.name.trim().length > 0,
    content: (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="team-form-name" className={labelCls}>
            {t('gest:admin.teams.form.name')}
          </label>
          <input
            id="team-form-name"
            autoFocus
            maxLength={200}
            placeholder={t('gest:admin.teams.form.namePlaceholder')}
            value={values.name}
            onChange={(e) => set({ name: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="team-form-department" className={labelCls}>
            {t('gest:admin.teams.form.department')}
          </label>
          <input
            id="team-form-department"
            maxLength={200}
            value={values.department}
            onChange={(e) => set({ department: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </div>
      </div>
    ),
  };

  const metaStep: GsWizardStep = {
    id: 'meta',
    title: t('gest:admin.teams.form.stepMeta'),
    content: (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5 sm:max-w-32">
          <label htmlFor="team-form-headcount" className={labelCls}>
            {t('gest:admin.teams.form.headcount')}
          </label>
          <input
            id="team-form-headcount"
            type="number"
            min={0}
            value={values.headcount}
            onChange={(e) => set({ headcount: e.target.value })}
            className={GS_COMPONENTS.input}
          />
        </div>
        {mode === 'edit' ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="team-form-director" className={labelCls}>
              {t('gest:admin.teams.form.director')}
            </label>
            <GsSelect
              value={values.directorEmail}
              onChange={(directorEmail) => set({ directorEmail })}
              options={directorOptions}
              placeholder={t('gest:admin.teams.form.directorNone')}
              ariaLabel={t('gest:admin.teams.form.director')}
            />
            <p className="text-meta text-slate-400 dark:text-slate-500">
              {t('gest:admin.teams.form.directorHint')}
            </p>
          </div>
        ) : null}
      </div>
    ),
  };

  return (
    <GsWizardDialog
      open={open}
      onClose={onClose}
      title={
        mode === 'create'
          ? t('gest:admin.teams.form.createTitle')
          : t('gest:admin.teams.form.editTitle', { name: initial?.name ?? '' })
      }
      steps={[identityStep, metaStep]}
      isLoading={isPending}
      previousLabel={t('gest:admin.teams.form.back')}
      nextLabel={t('gest:admin.teams.form.next')}
      cancelLabel={t('gest:admin.teams.form.cancel')}
      pendingLabel={t('gest:admin.teams.form.pending')}
      completeLabel={
        mode === 'create'
          ? t('gest:admin.teams.form.submitCreate')
          : t('gest:admin.teams.form.submitEdit')
      }
      onComplete={() =>
        onSubmit({
          name: values.name.trim(),
          department: values.department.trim() ? values.department.trim() : null,
          headcount: parseHeadcount(values.headcount),
          directorEmail: values.directorEmail ? values.directorEmail : null,
        })
      }
    />
  );
}
